import {
  B,
  PrimaryAction,
  SecondaryAction,
  SupportingText,
  Text,
  colors,
} from '@ohgoodlord/design-system'
import styled from 'styled-components'
import { cardLikeStyle } from '../../../components/design-system/utils'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { SupportedFileGroups } from '../../../components/file/utils'
import { useEffect, useRef, useState } from 'react'
import {
  addBlockDefaultDragEventBehaviourListeners,
  removeBlockDefaultDragEventBehaviourListeners,
} from '../../../common/utils'

const DropZone = styled.div<{ $isMobile: boolean }>`
  background-color: #f2f2f2;
  border-radius: 10px;
  padding: 0;
  min-height: 200px;
  height: 200px;
  height: fit-content;
  width: 100%;
  cursor: pointer;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  border: ${props => (props.$isMobile ? '' : '2px dashed #bdbdbd')};
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 24px 0;
  position: relative;
`

const ActionsWrapper = styled.div<{ $isMobile: boolean }>`
  min-width: 320px;
  pointer-events: all;
  margin-top: ${props => (props.$isMobile ? '10px' : '0')};
`

const SecondaryActionWrapper = styled.div`
  > button {
    border: 1.5px solid #257da2;
    border-radius: 4px;
    padding-left: 20px;
    padding-right: 20px;
  }
`

const StyledFileIcon = styled.div<{ $isMobile: boolean }>`
  ${cardLikeStyle()};
  background-color: ${colors.white};
  display: flex;
  border-radius: 100px;
  height: 78px;
  width: 78px;
  justify-content: center;
  margin-top: ${props => (props.$isMobile ? '-14px' : '')};
  align-items: center;
`
const PreviewWrapper = styled.div`
  max-height: 400px;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
`

const ImageWithPreservedAspectRatio = styled.div<{ $imgPath: string }>`
  height: auto;
  width: auto;
  background-image: url(${props => props.$imgPath});
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  flex-grow: 1;
`

const InvisibleImage = styled.img`
  max-height: 250px;
  height: auto;
  width: auto;
  visibility: hidden;
`

const actionButtonDesktopCopy = 'Select from your computer'
const actionButtonMobileCopy = `Select or take a photo`
const actionButtonPdfCopy = 'Select document'

export type InputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  full?: boolean
  medium?: boolean
  light?: boolean
  ref?: React.ForwardedRef<HTMLInputElement>
}

export interface Props {
  fileName: SupportedFileGroups | string
  setFiles: (files: File[]) => void
  isMobile: boolean
  allowMultipleFiles?: boolean
  withFilePreview?: boolean
  currentFileUrl?: string | undefined
  isPdf?: boolean
  currentFileIsPdf?: boolean
  uploadText?: string
  fileInputId?: string
  fileTypeHelperText?: string
}

export const FileUploadCard = ({
  fileName,
  setFiles,
  isMobile,
  allowMultipleFiles = true,
  withFilePreview,
  currentFileUrl,
  isPdf,
  currentFileIsPdf,
  uploadText,
  fileInputId,
  fileTypeHelperText,
}: Props) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [hasUploadedFiles, setHasUploadedFiles] = useState<boolean>(false)

  const handleFileChange = (files: File[]) => {
    if (files.length > 0) {
      setFiles(files)
      setHasUploadedFiles(true)
    }
  }

  useEffect(() => {
    addBlockDefaultDragEventBehaviourListeners()

    return () => {
      removeBlockDefaultDragEventBehaviourListeners()
    }
  }, [])

  const actionCopy = isPdf
    ? actionButtonPdfCopy
    : isMobile
    ? actionButtonMobileCopy
    : actionButtonDesktopCopy

  return (
    <DropZone
      onDrop={e => {
        e.preventDefault()
        e.stopPropagation()
        handleFileChange(Array.from(e.dataTransfer.files ?? []))
      }}
      onClick={() => {
        fileInputRef?.current?.click()
      }}
      $isMobile={isMobile}
    >
      <input
        type="file"
        ref={fileInputRef}
        multiple={allowMultipleFiles}
        accept={isPdf ? 'application/pdf' : 'image/x-png,image/jpeg,application/pdf'}
        onChange={e => handleFileChange(Array.from(e.target.files ?? []))}
        id={fileInputId ?? 'file-input'}
        data-testid="file-input"
        style={{ opacity: 0 }}
      />
      {withFilePreview && currentFileUrl !== undefined && !isPdf && !currentFileIsPdf ? (
        <PreviewWrapper>
          <ImageWithPreservedAspectRatio
            $imgPath={currentFileUrl}
            onClick={e => {
              e.stopPropagation()
              e.preventDefault()
              fileInputRef?.current?.click()
            }}
          >
            <InvisibleImage src={currentFileUrl} />
          </ImageWithPreservedAspectRatio>
        </PreviewWrapper>
      ) : (
        <>
          <StyledFileIcon $isMobile={isMobile}>
            <FontAwesomeIcon color="#2F80ED" icon="file-text" size="2x" />
          </StyledFileIcon>
          {!isMobile && uploadText === undefined && (
            <>
              <Text>
                <B>Drag {fileName.toLocaleLowerCase()} here</B>
              </Text>
              <SupportingText>or</SupportingText>
            </>
          )}
          {uploadText !== undefined && (
            <Text>
              <B>{uploadText}</B>
            </Text>
          )}
          <ActionsWrapper $isMobile={isMobile}>
            {hasUploadedFiles ? (
              <SecondaryActionWrapper>
                <SecondaryAction
                  onClick={e => {
                    e.stopPropagation()
                    e.preventDefault()
                    fileInputRef?.current?.click()
                  }}
                  isFullWidth
                >
                  {actionCopy}
                </SecondaryAction>
              </SecondaryActionWrapper>
            ) : (
              <PrimaryAction
                onClick={e => {
                  e.stopPropagation()
                  e.preventDefault()
                  fileInputRef?.current?.click()
                }}
                isFullWidth
              >
                {actionCopy}
              </PrimaryAction>
            )}
          </ActionsWrapper>
          <SupportingText>
            {fileTypeHelperText ??
              (isPdf ? 'PDF files only' : 'PDF or a photo of paper documents')}
          </SupportingText>
          {isPdf ? (
            <SupportingText>Screenshots and photos won&apos;t be accepted</SupportingText>
          ) : (
            <SupportingText>We can&apos;t accept screenshots</SupportingText>
          )}
        </>
      )}
    </DropZone>
  )
}
